import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, UserX, Mail, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

type StuckReason = "no_credentials" | "pending_review_stale";

interface StuckProvider {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  granted_at: string | null;
  reason: StuckReason;
  cred_updated_at?: string | null;
}

interface Props {
  onNavigateToTab?: (tab: string) => void;
}

const STALE_REVIEW_DAYS = 7;

const StuckProviderAccounts = ({ onNavigateToTab }: Props) => {
  const [stuck, setStuck] = useState<StuckProvider[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: providerRoles }, { data: creds }, { data: emails }] = await Promise.all([
        supabase.from("user_roles").select("user_id, created_at").eq("role", "provider"),
        supabase
          .from("provider_credentials")
          .select("user_id, credential_status, updated_at, created_at"),
        supabase.rpc("get_user_emails"),
      ]);

      const credMap = new Map<string, any>((creds || []).map((c: any) => [c.user_id, c]));
      const emailMap = new Map((emails || []).map((e: any) => [e.user_id, e.email]));

      // Group 1: providers with role but no credentials row
      const missing = (providerRoles || []).filter((r: any) => !credMap.has(r.user_id));

      // Group 2: any credentials row stuck in pending_review for 7+ days
      const cutoff = Date.now() - STALE_REVIEW_DAYS * 24 * 60 * 60 * 1000;
      const stale = (creds || []).filter((c: any) => {
        if (c.credential_status !== "pending_review") return false;
        const ts = new Date(c.updated_at || c.created_at).getTime();
        return ts <= cutoff;
      });

      const allIds = Array.from(
        new Set([
          ...missing.map((m: any) => m.user_id),
          ...stale.map((s: any) => s.user_id),
        ])
      );

      if (allIds.length === 0) {
        setStuck([]);
        return;
      }

      const { data: profiles } = await supabase
        .from("profiles")
        .select("user_id, first_name, last_name")
        .in("user_id", allIds);

      const profileMap = new Map((profiles || []).map((p: any) => [p.user_id, p]));
      const grantedMap = new Map(
        (providerRoles || []).map((r: any) => [r.user_id, r.created_at])
      );

      const missingRows: StuckProvider[] = missing.map((r: any) => ({
        user_id: r.user_id,
        first_name: profileMap.get(r.user_id)?.first_name || null,
        last_name: profileMap.get(r.user_id)?.last_name || null,
        email: emailMap.get(r.user_id) || null,
        granted_at: r.created_at,
        reason: "no_credentials",
      }));

      const staleRows: StuckProvider[] = stale.map((c: any) => ({
        user_id: c.user_id,
        first_name: profileMap.get(c.user_id)?.first_name || null,
        last_name: profileMap.get(c.user_id)?.last_name || null,
        email: emailMap.get(c.user_id) || null,
        granted_at: grantedMap.get(c.user_id) || null,
        reason: "pending_review_stale",
        cred_updated_at: c.updated_at || c.created_at,
      }));

      setStuck([...missingRows, ...staleRows]);
    } catch (err: any) {
      toast.error("Failed to load stuck providers", { description: err?.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleRevoke = async (userId: string, name: string) => {
    if (!confirm(`Revoke provider role for ${name}? They'll need to re-register.`)) return;
    try {
      const { error } = await supabase.rpc("revoke_provider", {
        target_user_id: userId,
        reason: "no_credentials",
      });
      if (error) throw error;
      toast.success("Provider role revoked");
      load();
    } catch (err: any) {
      toast.error("Failed to revoke", { description: err?.message });
    }
  };

  const handleResend = async (provider: StuckProvider, name: string) => {
    if (!provider.email) {
      toast.error("No email on file for this provider");
      return;
    }
    try {
      const { error } = await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "provider-onboarding-reminder",
          recipientEmail: provider.email,
          idempotencyKey: `provider-onboarding-reminder-${provider.user_id}-${Date.now()}`,
          templateData: { providerName: name },
        },
      });
      if (error) throw error;
      toast.success(`Onboarding reminder sent to ${provider.email}`);
    } catch (err: any) {
      toast.error("Failed to send reminder", { description: err?.message });
    }
  };

  const daysSince = (iso?: string | null) => {
    if (!iso) return null;
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / (24 * 60 * 60 * 1000));
    return days;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Stuck Provider Accounts</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">Checking…</CardContent>
      </Card>
    );
  }

  if (stuck.length === 0) {
    return null;
  }

  return (
    <Card className="border-destructive/30">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} className="text-destructive" />
          <CardTitle className="text-base">Stuck Provider Accounts</CardTitle>
          <Badge variant="destructive">{stuck.length}</Badge>
        </div>
        <Button variant="ghost" size="sm" onClick={load}>
          <RefreshCw size={14} />
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-muted-foreground mb-3">
          Providers blocked during onboarding or waiting too long for credential review.
        </p>
        {stuck.map((p) => {
          const name = [p.first_name, p.last_name].filter(Boolean).join(" ") || p.email || p.user_id.slice(0, 8);
          const isStale = p.reason === "pending_review_stale";
          const waitingDays = isStale ? daysSince(p.cred_updated_at) : null;
          return (
            <div
              key={`${p.reason}-${p.user_id}`}
              className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/30 px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate">{name}</span>
                  {isStale ? (
                    <Badge variant="secondary">
                      Stuck in review {waitingDays != null ? `· ${waitingDays}d` : ""}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-destructive border-destructive/40">
                      No credentials
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {p.email || "no email"} · role granted{" "}
                  {p.granted_at ? new Date(p.granted_at).toLocaleDateString() : "—"}
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                {isStale ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onNavigateToTab?.("credentialing")}
                  >
                    <ClipboardCheck size={14} className="mr-1" />
                    Review credentials
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleResend(p, name)}
                      disabled={!p.email}
                    >
                      <Mail size={14} className="mr-1" />
                      Resend onboarding email
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRevoke(p.user_id, name)}
                    >
                      <UserX size={14} className="mr-1" />
                      Revoke role
                    </Button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default StuckProviderAccounts;
